<script>
  import Async from '../components/Async.svelte';
  import Section from '../components/Section.svelte';
  import Panel from '../components/Panel.svelte';
  import Button from '../components/Button.svelte';
  import HelpDrawer from '../components/HelpDrawer.svelte';
  import { getSchemas, changeSchema } from '../lib/api.js';
  import { can } from '../lib/session.svelte.js';
  import { HELP } from '../lib/help.js';

  // The institution's schemas (docs/ref-belonging-plan.md, step 5.6.4): ours,
  // the draft made from files, what it reuses from partners and the copy of
  // core, each term with where it is used. Schemas are written offline as
  // Turtle; here one is uploaded, replaced, taken from a partner or put back
  // to its previous version, each checked first against what uses it.
  let schemas = $state(getSchemas());
  const reload = () => { schemas = getSchemas(); };
  let filter = $state('');

  const KINDS = {
    ours: 'Ours',
    draft: 'Draft, made here from files',
    copy: 'Kept here as a copy',
    reused: 'Reused from a partner',
  };
  const ABOUT = {
    ours: 'This institution publishes this schema. Partners who use it are comparable with it straight away.',
    draft: 'Made from the columns of the files datasets were started from: one term per column, named after it. Download it, give its terms names and wording of your own, and upload it as a new version.',
    copy: 'SolidShare core, published by the network. Kept here unchanged so it can be read next to the schemas that build on it.',
    reused: 'A partner publishes this schema; this institution uses its terms as they are.',
  };
  const SHARED = { drop: 'never', coarsen: 'once grouped', isolate: 'on its own', open: 'freely' };

  const matches = (q, ...texts) => !q || texts.some((t) => t && String(t).toLowerCase().includes(q));
  const termsOf = (g, q) => g.terms.filter((t) => matches(q, t.id, t.text, t.means, t.section, t.construct,
    ...t.columns.map((c) => c.column)));
  const used = (t) => t.columns.length || t.cubes.length;
  const local = (id) => id.slice(id.indexOf(':') + 1);
  const several = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

  // One change at a time: checked first, then carried out on confirmation.
  // `request` is what POST /api/schemas gets; `result` its check.
  let pending = $state(null);
  let busy = $state(false);
  let failed = $state('');
  let done = $state('');
  async function check(request, label) {
    failed = ''; done = ''; busy = true;
    try {
      pending = { request, label, result: await changeSchema(request) };
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) { failed = e.message; } finally { busy = false; }
  }
  async function apply() {
    busy = true; failed = '';
    try {
      const result = await changeSchema({ ...pending.request, apply: true });
      done = `${pending.label}: done. ${result.id} is now at version ${result.schema.version}.`;
      pending = null;
      reload();
    } catch (e) { failed = e.message; } finally { busy = false; }
  }
  async function fromFile(event, action, id = null) {
    const file = event.currentTarget.files?.[0];
    event.currentTarget.value = '';
    if (!file) return;
    const turtle = await file.text();
    const prefix = id ?? file.name.replace(/\.ttl$/i, '');
    check({ action, id: prefix, turtle }, action === 'upload' ? `Upload ${prefix} from ${file.name}` : `Replace ${prefix} with ${file.name}`);
  }
</script>

<HelpDrawer {...HELP.schemas} />

<h1 class="wf2-title wf2-title-level-1">Schemas</h1>
<p class="wf2-subtitle">The terms the data is described with, one schema at a time: ours, a draft made from files,
  and those reused from partners. A schema is written offline as a Turtle file and uploaded here; before it
  replaces another, it is checked against everything that uses it.</p>

{#if pending}
  {@const r = pending.result}
  <Section title={pending.label}>
    <Panel>
      <p class="wf2-text"><span class="mono">{r.id}</span>{#if r.schema.title} · {r.schema.title}{/if}{#if r.schema.publisher} · {r.schema.publisher}{/if}
        · version {r.current ? `${r.current} → ` : ''}{r.schema.version ?? '—'}</p>
      {#if r.problems.length}
        <p class="wf2-text">It can't be used as it is:</p>
        <ul class="wf2-text">{#each r.problems as problem}<li>{problem[0].toUpperCase()}{problem.slice(1)}.</li>{/each}</ul>
        <Button variant="secondary" onclick={() => { pending = null; }}>Close</Button>
      {:else}
        <ul class="wf2-text">
          {#if r.changes.added.length}<li>{several(r.changes.added.length, 'new term')}</li>{/if}
          {#if r.changes.reworded.length}<li>{several(r.changes.reworded.length, 'term')} with new wording</li>{/if}
          {#if r.changes.removed.length}<li>{several(r.changes.removed.length, 'term')} no longer in it, none of them in use: {r.changes.removed.join(', ')}</li>{/if}
          {#if !r.changes.added.length && !r.changes.reworded.length && !r.changes.removed.length}<li>No term added, removed or reworded.</li>{/if}
          <li>Nothing that uses its terms breaks.</li>
        </ul>
        <div class="actions">
          <Button variant="primary" disabled={busy} onclick={apply}>{busy ? 'Working…' : 'Go ahead'}</Button>
          <Button variant="secondary" onclick={() => { pending = null; }}>Cancel</Button>
        </div>
      {/if}
    </Panel>
  </Section>
{/if}
{#if failed}<p class="wf2-text" role="alert">{failed}</p>{/if}
{#if done}<p class="wf2-text" role="status">{done}</p>{/if}

<Async promise={schemas}>
  {#snippet children({ groups, housekeeping, partners, publicPage })}
    <p class="wf2-text">Anyone can read them at <a href={publicPage} target="_blank" rel="noopener">{publicPage}</a>,
      which is where each term's IRI leads.</p>

    {#if can('map')}
      <Section title="Add a schema">
        <Panel>
          <label class="wf2-field">Upload one of our own
            <input class="wf2-control" type="file" accept=".ttl,text/turtle" disabled={busy}
              onchange={(e) => fromFile(e, 'upload')} /></label>
          <p class="wf2-hint">A Turtle file named after its prefix (<span class="mono">tickets.ttl</span>), with a title,
            a publisher and a version. How to write one: docs/schema-pack.md.</p>
          {#each partners as p}
            {@const offered = p.schemas.filter((s) => s.status === 'new' || s.status === 'newer')}
            {#if p.problem}
              <p class="wf2-hint">{p.label}: {p.problem}.</p>
            {:else if offered.length}
              <h3 class="wf2-title wf2-title-level-3">From {p.label}</h3>
              <ul class="wf2-text">
                {#each offered as s}
                  <li><span class="mono">{s.id}</span> · {s.title ?? s.id} · version {s.version}
                    {#if s.status === 'newer'}(we use {s.held}){/if}
                    <Button variant="secondary" disabled={busy}
                      onclick={() => check({ action: 'take', id: s.id, webId: p.webId },
                        s.status === 'newer' ? `Update ${s.id} to ${s.version}` : `Take ${s.id} from ${p.label}`)}>
                      {s.status === 'newer' ? `Update to ${s.version}` : 'Take it'}</Button></li>
                {/each}
              </ul>
            {/if}
          {/each}
        </Panel>
      </Section>
    {/if}

    <div class="filter">
      <label class="wf2-caption" for="vocab-filter">Find a term</label>
      <input id="vocab-filter" class="wf2-input" type="search" placeholder="Name, wording or column"
        bind:value={filter} autocomplete="off" />
    </div>
    {@const q = filter.trim().toLowerCase()}
    {#each groups as g (g.prefix)}
      {@const terms = termsOf(g, q)}
      {#if !q || terms.length}
        <Section title={`${g.title ?? g.prefix} · ${KINDS[g.kind]}`}>
          <Panel>
            <p class="wf2-text">{ABOUT[g.kind]}</p>
            <p class="wf2-caption"><span class="mono">{g.prefix}</span>{#if g.publisher} · {g.publisher}{/if}{#if g.version} · version {g.version}{/if}
              {#if g.namespace}· <span class="mono">{g.namespace}</span>{/if}
              {#if g.kind !== 'reused'}· <a href={`${publicPage}${g.prefix}`} target="_blank" rel="noopener">public page</a>
                · <a href={`${publicPage}${g.prefix}.ttl`} download={`${g.prefix}.ttl`}>Download {g.prefix}.ttl</a>{/if}</p>
            {#if can('map') && (g.kind === 'ours' || g.kind === 'draft' || g.previous)}
              <div class="actions">
                {#if g.kind === 'ours' || g.kind === 'draft'}
                  <label class="wf2-caption">Replace with a new version
                    <input class="wf2-control" type="file" accept=".ttl,text/turtle" disabled={busy}
                      onchange={(e) => fromFile(e, 'replace', g.prefix)} /></label>
                {/if}
                {#if g.previous}
                  <Button variant="secondary" disabled={busy}
                    onclick={() => check({ action: 'undo', id: g.prefix }, `Put ${g.prefix} back to version ${g.previous}`)}>
                    Back to version {g.previous}</Button>
                {/if}
              </div>
            {/if}

            {#if !q && g.recordClasses.length}
              <h3 class="wf2-title wf2-title-level-3">Record classes</h3>
              <div class="wf2-tablewrap"><table class="wf2-table">
                <thead><tr><th>Class</th><th>Dataset</th><th>Terms</th></tr></thead>
                <tbody>
                  {#each g.recordClasses as rc}
                    <tr>
                      <td>{rc.text ?? local(rc.id)}<span class="col-stats mono">{rc.id}</span></td>
                      <td>{#if rc.dataset}<a href={`#/data/dataset?id=${encodeURIComponent(rc.dataset.id)}`}>{rc.dataset.label}</a>{:else}—{/if}</td>
                      <td>{rc.terms}</td>
                    </tr>
                  {/each}
                </tbody>
              </table></div>
            {/if}

            {#if terms.length}
              <h3 class="wf2-title wf2-title-level-3">Terms{#if q}{` matching “${filter.trim()}”`}{/if}</h3>
              <div class="wf2-tablewrap"><table class="wf2-table">
                <thead><tr><th>Term</th><th>Values</th><th>Shared</th><th>Filled from</th><th>Counted in</th></tr></thead>
                <tbody>
                  {#each terms as t (t.id)}
                    <tr class:unused={!used(t)}>
                      <td>
                        {t.text ?? local(t.id)}
                        <span class="col-stats mono">{t.id}</span>
                        {#if t.section || t.construct || t.dataset}
                          <span class="col-stats">{[t.dataset?.label, t.section, t.construct].filter(Boolean).join(' · ')}</span>
                        {/if}
                      </td>
                      <td>{#if t.valueScheme}{t.valueScheme.label ?? local(t.valueScheme.id)}{#if t.valueScheme.size}<span class="col-stats">{t.valueScheme.size} values</span>{/if}{:else}{t.unit ?? '—'}{/if}</td>
                      <td>{t.treatment ? SHARED[t.treatment] ?? t.treatment : '—'}</td>
                      <td>{#each t.columns as c}<span class="col-stats mono">{c.source}.{c.column}</span>{:else}—{/each}</td>
                      <td>{#each t.cubes as cube}<span class="col-stats mono">{cube}</span>{:else}—{/each}</td>
                    </tr>
                  {/each}
                </tbody>
              </table></div>
            {/if}

            {#if !q && g.valueSchemes.length}
              <details class="drop-list">
                <summary>Value lists ({g.valueSchemes.length})</summary>
                <div class="wf2-tablewrap"><table class="wf2-table">
                  <thead><tr><th>List</th><th>Values</th><th>Used by</th></tr></thead>
                  <tbody>
                    {#each g.valueSchemes as s}
                      <tr>
                        <td>{s.label ?? local(s.id)}<span class="col-stats mono">{s.id}</span></td>
                        <td>{s.values.join(' · ') || (s.typed ? 'any value' : '—')}</td>
                        <td>{s.usedBy.length} {s.usedBy.length === 1 ? 'term' : 'terms'}</td>
                      </tr>
                    {/each}
                  </tbody>
                </table></div>
              </details>
            {/if}

          </Panel>
        </Section>
      {/if}
    {/each}
    {#if !q && housekeeping.length}
      <Section title="On every record">
        <Panel>
          <p class="wf2-text">Besides its terms, every record carries these, written by SolidShare when it is stored.</p>
          <div class="wf2-tablewrap"><table class="wf2-table">
            <tbody>
              {#each housekeeping as h}
                <tr><td class="mono">{h.id}</td><td>{h.text}</td></tr>
              {/each}
            </tbody>
          </table></div>
        </Panel>
      </Section>
    {/if}
    {#if q && !groups.some((g) => termsOf(g, q).length)}
      <p class="wf2-text">No term matches “{filter.trim()}”.</p>
    {/if}
  {/snippet}
</Async>

<style>
  .actions { display: flex; flex-wrap: wrap; gap: var(--space-3); align-items: end; margin: var(--space-2) 0; }
  .filter { display: flex; flex-direction: column; gap: 4px; max-width: 420px; margin-bottom: var(--space-4); }
  tr.unused td { color: var(--muted); }
</style>
